/**
 * CustomIntake — patient-facing renderer for custom (form-builder) intakes.
 *
 * URL: /patient/intake/custom/:token
 *
 * Flow:
 *   1. Validate the token via /api/intakes/validate/:token. The response
 *      includes { renderer, intakeFormTemplate: { schema } } for custom forms.
 *   2. Render the schema with the same IntakeFormRenderer the builder uses.
 *   3. Autosave drafts to /api/intakes/draft/:token.
 *   4. Submit to /api/intakes/submit/:token with { responses }.
 *
 * Uses public endpoints (no auth header) so the API_BASE_URL is hit directly.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import {
  IntakeFormRenderer,
  validateResponses,
  type ResponseMap,
} from '@/components/intake-builder/IntakeFormRenderer';
import type { IntakeSection } from '@/lib/intake/templates/types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';

interface ValidatedIntake {
  intakeId: string;
  patientName: string | null;
  providerName: string;
  clinicName: string | null;
  expiresAt: string;
  renderer: 'system' | 'custom';
  intakeFormTemplate: {
    id: string;
    name: string;
    description: string | null;
    schema: { sections: IntakeSection[] };
    version: number;
    estimatedMinutes: number | null;
  } | null;
  hasDraft: boolean;
  draftData: Record<string, any> | null;
}

const fetchJson = async (url: string, init?: RequestInit) => {
  const res = await fetch(`${API_BASE_URL}${url}`, {
    headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
    ...init,
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body.success === false) {
    throw new Error(body.message || body.error || `Request failed (${res.status})`);
  }
  return body;
};

const CustomIntake: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [intake, setIntake] = useState<ValidatedIntake | null>(null);
  const [responses, setResponses] = useState<ResponseMap>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const draftTimer = useRef<number | null>(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const validateRes = await fetchJson(`/intakes/validate/${token}`);
        if (cancelled) return;
        const data = validateRes.data;

        // System (hardcoded therapy) intakes don't belong here — bounce back.
        if (data.renderer === 'system') {
          const tc = data.therapyCategory;
          const path = tc === 'trt' ? `/patient/intake/mens/${token}`
            : tc === 'hrt' ? `/patient/intake/womens/${token}`
            : tc === 'glp1' ? `/patient/intake/weightloss/${token}`
            : `/patient/intake/${token}`;
          navigate(path, { replace: true });
          return;
        }

        if (!data.intakeFormTemplate) {
          setError('This intake link is no longer valid.');
          setLoading(false);
          return;
        }

        setIntake(data);
        // Hydrate from any existing draft.
        if (data.hasDraft && data.draftData?.responses) {
          setResponses(data.draftData.responses);
        }
        setLoading(false);
      } catch (err: any) {
        if (cancelled) return;
        setError(err.message || 'Failed to load intake');
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, navigate]);

  // Autosave draft on responses change (debounced 800ms).
  useEffect(() => {
    if (!intake || !token) return;
    if (draftTimer.current) window.clearTimeout(draftTimer.current);
    draftTimer.current = window.setTimeout(() => {
      fetchJson(`/intakes/draft/${token}`, {
        method: 'POST',
        body: JSON.stringify({ data: { responses } }),
      }).catch(() => { /* best effort */ });
    }, 800);
    return () => {
      if (draftTimer.current) window.clearTimeout(draftTimer.current);
    };
  }, [responses, intake, token]);

  const sections = useMemo(
    () => intake?.intakeFormTemplate?.schema?.sections || [],
    [intake],
  );

  const handleSubmit = async () => {
    if (!token || !intake) return;
    const validation = validateResponses(sections, responses);
    setErrors(validation);
    if (Object.keys(validation).length > 0) {
      const firstError = document.querySelector('[id^="q-"]');
      firstError?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setSubmitting(true);
    try {
      await fetchJson(`/intakes/submit/${token}`, {
        method: 'POST',
        body: JSON.stringify({ responses }),
      });
      setSubmitted(true);
    } catch (err: any) {
      setError(err.message || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md text-center space-y-3">
          <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto" />
          <h1 className="text-xl font-semibold text-slate-900">Can't open this intake</h1>
          <p className="text-sm text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md text-center space-y-3 rounded-lg border border-slate-200 bg-white p-8">
          <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
          <h1 className="text-xl font-semibold text-slate-900">Thanks — your intake is submitted</h1>
          <p className="text-sm text-slate-600">
            {intake?.providerName} will review your responses before your appointment.
          </p>
        </div>
      </div>
    );
  }

  if (!intake?.intakeFormTemplate) return null;

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-3xl mx-auto space-y-6">
        <header className="space-y-1">
          {intake.clinicName && (
            <p className="text-xs uppercase tracking-wide text-slate-500">{intake.clinicName}</p>
          )}
          <h1 className="text-2xl font-semibold text-slate-900">
            {intake.intakeFormTemplate.name}
          </h1>
          {intake.intakeFormTemplate.description && (
            <p className="text-sm text-slate-600">{intake.intakeFormTemplate.description}</p>
          )}
          <p className="text-xs text-slate-500">
            For {intake.patientName || 'you'} · {intake.providerName}
            {intake.intakeFormTemplate.estimatedMinutes
              ? ` · ~${intake.intakeFormTemplate.estimatedMinutes} min`
              : ''}
          </p>
        </header>

        <div className="rounded-lg border border-slate-200 bg-white p-6">
          <IntakeFormRenderer
            sections={sections}
            responses={responses}
            onChange={(qid, v) => setResponses(prev => ({ ...prev, [qid]: v }))}
            disabled={submitting}
            errors={errors}
          />
        </div>

        <div className="flex justify-end">
          <Button onClick={handleSubmit} disabled={submitting} size="lg">
            {submitting ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting…</>
            ) : 'Submit'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CustomIntake;
